import COLORS from "shared/config/colors";
import Text from "shared/components/data-display/text/Text";
import {
    createCalendar,
    getWeeksInMonth,
    isToday,
} from "@internationalized/date";
import {
    useCalendar,
    useCalendarCell,
    useCalendarGrid,
} from "@react-aria/calendar";

import PropTypes from "prop-types";
import React from "react";
import Tooltip from "shared/components/utils/tooltip/Tooltip";
import moment from "moment";
import { useCalendarState } from "@react-stately/calendar";
import { useLocale } from "@react-aria/i18n";
import {
    StyledWrapper,
    CardTitle,
    DateButton,
} from "./DatePickerCalendarCard.styles";

export const CalendarTitleBlockPadding = "19px 16px";

function CalendarCell({ state, holidaysList, date }) {
    const ref = React.useRef();
    const { cellProps, isOutsideVisibleRange, formattedDate } = useCalendarCell(
        { date },
        state,
        ref
    );

    const currentDate = moment(`${date.year}-${date.month}-${date.day}`).format("YYYY-MM-DD");

    const holidayObj = !isOutsideVisibleRange
        && holidaysList.find(x => {
            const holiday = moment(x.Date).format("YYYY-MM-DD");
            return moment(holiday).isSame(currentDate);
        });

    const tooltipText = holidayObj?.Name ?? "";

    return (
        <td {...cellProps}>
            <Tooltip enterDelay={0} text={tooltipText}>
                <div>
                    <DateButton
                        ref={ref}
                        isHoliday={holidayObj}
                        isCurrent={isToday(date)}
                        isDisabled={isOutsideVisibleRange}
                    >
                        {formattedDate}
                    </DateButton>
                </div>
            </Tooltip>
        </td>
    );
}

function CalendarGrid({ state, holidaysList, ...props }) {
    const { locale } = useLocale();
    const { gridProps, headerProps, weekDays } = useCalendarGrid(props, state);

    const weeksInMonth = getWeeksInMonth(state.visibleRange.start, locale);

    return (
        <table
            {...gridProps}
            style={{ padding: "0 7px 0 16px", margin: "0 auto" }}
        >
            <thead {...headerProps}>
                <tr>
                    {weekDays.map((day, index) => (
                        <th
                            key={`${day}-${index}`}
                            style={{
                                width: "33px",
                                paddingBottom: "7px",
                                paddingRight: "9px",
                                textAlign: "center",
                            }}
                        >
                            <Text
                                variant="Descriptive_1_1"
                                color={COLORS.Gray_3}
                            >
                                {day}
                            </Text>
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {[...new Array(weeksInMonth).keys()].map((weekIndex) => (
                    <tr key={weekIndex}>
                        {state
                            .getDatesInWeek(weekIndex)
                            .map((date, i) =>
                                date ? (
                                    <CalendarCell
                                        key={`${date.year}-${date.month}-${date.day}`}
                                        state={state}
                                        holidaysList={holidaysList}
                                        date={date}
                                    />
                                ) : (
                                    <td key={`empty-${weekIndex}-${i}`} />
                                )
                            )}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function DatePickerCalendarCard({
    holidaysList = [],
    ...props
}) {
    const { locale } = useLocale();
    const state = useCalendarState({
        ...props,
        locale,
        createCalendar,
    });
    const ref = React.useRef();
    const { calendarProps, title } = useCalendar(props, state, ref);

    return (
        <StyledWrapper {...calendarProps} ref={ref}>
            <CardTitle variant={"Headline_5"} color={COLORS.Gray_1}>
                {title?.split(" ", 1)[0]}
            </CardTitle>

            <CalendarGrid state={state} holidaysList={holidaysList} />
        </StyledWrapper>
    );
}

CalendarCell.propTypes = {
    state: PropTypes.object,
    holidaysList: PropTypes.array,
    date: PropTypes.object,
};

CalendarGrid.propTypes = {
    state: PropTypes.object,
    holidaysList: PropTypes.array,
};

DatePickerCalendarCard.propTypes = {
    holidaysList: PropTypes.array,
};

export default DatePickerCalendarCard;
